/**
 * Task 5 — "Name your workspace" gate (WorkspaceNameSetupScreen, App.tsx).
 *
 * Restricts the first-run naming prompt to un-named TEAM workspaces only.
 * Solo workspaces are auto-named at creation (Task 3), so `nameSetByUser`
 * is already true for them and they never reach this gate. The gate keys on
 * `nameSetByUser` (surfaced from the DB via Task 4 / GET /auth/containers)
 * rather than inferring name state from "Personal" heuristics or recency
 * windows.
 *
 * Gate conditions (all must be true):
 *  - owner (`isOwner === true`)
 *  - team workspace (`type === 'team'`)
 *  - user has not yet set a name (`nameSetByUser !== true`)
 *  - not dismissed in-session (`!dismissed`)
 *  - not skipped via localStorage (`!localStorageSkipFlag`)
 */

export interface WorkspaceNameGateInput {
  /** Contract workspace type from the containers endpoint. */
  type: "solo" | "team" | undefined;
  isOwner: boolean | undefined;
  containerName?: string | null | undefined;
  /** Whether the workspace name was explicitly set by the user. */
  nameSetByUser: boolean | null | undefined;
  /** Workspace `created_at` (epoch ms), surfaced as `member_since`. */
  memberSince?: number | null | undefined;
  /**
   * Derived per-container flag — for owners this is just the user-level
   * `users.onboarding_completed` boolean (no membership row exists).
   */
  onboardingCompleted?: boolean | undefined;
  /** In-session dismissal (X / skip already clicked this session). */
  dismissed: boolean;
  /** Per-workspace localStorage skip flag (see hasWorkspaceNameSkipFlag). */
  localStorageSkipFlag: boolean;
  /** Injectable clock for tests (unused by the new predicate, kept for compat). */
  now?: number;
}

export function shouldShowWorkspaceNameSetup(
  input: WorkspaceNameGateInput,
): boolean {
  return (
    input.isOwner === true &&
    input.type === 'team' &&
    input.nameSetByUser !== true &&
    !input.dismissed &&
    !input.localStorageSkipFlag
  );
}

/**
 * localStorage keys for the per-workspace "user skipped naming" flag.
 * `xavior:team-named:<id>` is the legacy key written by the team-only gate
 * (#3949) — still honored so users who skipped before this generalization
 * are not re-prompted. New skips write the generalized key.
 */
export function workspaceNameSkipKeys(workspaceId: string): {
  current: string;
  legacy: string;
} {
  return {
    current: `xavior:workspace-named:${workspaceId}`,
    legacy: `xavior:team-named:${workspaceId}`,
  };
}

export function hasWorkspaceNameSkipFlag(
  workspaceId: string | null | undefined,
): boolean {
  if (typeof window === "undefined" || !workspaceId) return false;
  try {
    const keys = workspaceNameSkipKeys(workspaceId);
    return Boolean(
      localStorage.getItem(keys.current) || localStorage.getItem(keys.legacy),
    );
  } catch {
    return false;
  }
}

export function setWorkspaceNameSkipFlag(
  workspaceId: string | null | undefined,
): void {
  if (typeof window === "undefined" || !workspaceId) return;
  try {
    localStorage.setItem(workspaceNameSkipKeys(workspaceId).current, "1");
  } catch {
    /* noop */
  }
}
